<template>
  <video
    :src="playbackSrc || src"
    controls
    controlslist="nodownload"
    preload="metadata"
    webkit-playsinline
    playsinline
    @error="handleVideoError"
  ></video>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getOfflineMediaCacheName } from '../services/offlineMediaService';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
});

const playbackSrc = ref('');
let activeBlobUrl = null;

const toAbsoluteUrl = (value) => {
  if (!value) return null;

  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return null;
  }
};

const revokeBlobUrl = () => {
  if (activeBlobUrl) {
    URL.revokeObjectURL(activeBlobUrl);
    activeBlobUrl = null;
  }
};

const getCachedBlobUrl = async (sourceUrl) => {
  if (!('caches' in window)) {
    return null;
  }

  const cache = await caches.open(getOfflineMediaCacheName());
  const absoluteUrl = toAbsoluteUrl(sourceUrl);
  const candidates = [absoluteUrl, sourceUrl].filter(Boolean);

  for (const candidate of candidates) {
    const response = await cache.match(candidate);

    if (!response) {
      continue;
    }

    const blob = await response.blob();

    if (!blob || blob.size === 0) {
      continue;
    }

    return URL.createObjectURL(blob);
  }

  return null;
};

const refreshPlaybackSource = async ({ forceOffline = false } = {}) => {
  if (!props.src) {
    revokeBlobUrl();
    playbackSrc.value = '';
    return;
  }

  const shouldUseOfflineBlob = forceOffline || !navigator.onLine;

  revokeBlobUrl();

  if (shouldUseOfflineBlob) {
    const blobUrl = await getCachedBlobUrl(props.src);

    if (blobUrl) {
      activeBlobUrl = blobUrl;
      playbackSrc.value = blobUrl;
      return;
    }
  }

  playbackSrc.value = props.src;
};

const handleVideoError = async () => {
  if (navigator.onLine || !props.src) {
    return;
  }

  await refreshPlaybackSource({ forceOffline: true });
};

const handleOffline = () => {
  void refreshPlaybackSource({ forceOffline: true });
};

const handleOnline = () => {
  void refreshPlaybackSource();
};

watch(
  () => props.src,
  () => {
    void refreshPlaybackSource();
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);
});

onBeforeUnmount(() => {
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('online', handleOnline);
  revokeBlobUrl();
});
</script>
